import React, { createContext, useContext, useState, useCallback } from 'react';
import HallazgoDetailDrawer from 'components/admin/Trazabilidad/HallazgoDetailDrawer';
import BudgetSummaryDrawer from 'components/admin/Trazabilidad/BudgetSummaryDrawer';
import OrderSummaryDrawer from 'components/admin/Trazabilidad/OrderSummaryDrawer';
import VoucherSummaryDrawer from 'components/admin/Trazabilidad/VoucherSummaryDrawer';

// Fase 9 del roadmap Hallazgos->Presupuesto->ODT.
// Provider que expone un hook useTrazabilidadDrawer() con funciones para
// abrir cualquier drawer de detalle desde cualquier punto de la app. Los
// drawers son anidables (antd Drawer push offset automatico via zIndex
// incrementando). Stack de max 3 niveles para evitar abismos.
//
// Uso:
//   const { openHallazgo, openBudget, openOrder, openVoucher } = useTrazabilidadDrawer();
//   onClick={() => openHallazgo(hallazgoId)}
//
// Cada drawer interno dispara openX via props para navegar cruzadamente
// sin cerrar el anterior (push). El boton Close cierra solo el mas
// reciente (pop).

const TrazabilidadDrawerCtx = createContext(null);

const MAX_STACK_DEPTH = 3;

export function TrazabilidadDrawerProvider({ children }) {
  // Stack: [{ type: 'hallazgo'|'budget'|'order'|'voucher', id }]
  const [stack, setStack] = useState([]);

  const push = useCallback((entry) => {
    setStack((prev) => {
      if (prev.length >= MAX_STACK_DEPTH) {
        // Si se excede, reemplazar el tope (no abrir abismos infinitos).
        return [...prev.slice(0, -1), entry];
      }
      return [...prev, entry];
    });
  }, []);

  const pop = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const closeAll = useCallback(() => setStack([]), []);

  const openHallazgo = useCallback(
    (id, claveGrupo) => push({ type: 'hallazgo', id, claveGrupo }),
    [push]
  );
  const openBudget = useCallback((id) => push({ type: 'budget', id }), [push]);
  const openOrder = useCallback((id) => push({ type: 'order', id }), [push]);
  const openVoucher = useCallback((id) => push({ type: 'voucher', id }), [push]);

  const value = {
    openHallazgo,
    openBudget,
    openOrder,
    openVoucher,
    closeAll,
    stack,
  };

  return (
    <TrazabilidadDrawerCtx.Provider value={value}>
      {children}
      {stack.map((entry, idx) => {
        const isTop = idx === stack.length - 1;
        const handleClose = isTop ? pop : undefined; // solo el tope cierra
        const common = { open: true, onClose: handleClose, level: idx };
        switch (entry.type) {
          case 'hallazgo':
            return (
              <HallazgoDetailDrawer
                key={`h-${entry.id}-${idx}`}
                idHallazgo={entry.id}
                claveGrupo={entry.claveGrupo}
                {...common}
              />
            );
          case 'budget':
            return (
              <BudgetSummaryDrawer key={`b-${entry.id}-${idx}`} idBudget={entry.id} {...common} />
            );
          case 'order':
            return (
              <OrderSummaryDrawer key={`o-${entry.id}-${idx}`} idOrder={entry.id} {...common} />
            );
          case 'voucher':
            return (
              <VoucherSummaryDrawer key={`v-${entry.id}-${idx}`} idVoucher={entry.id} {...common} />
            );
          default:
            return null;
        }
      })}
    </TrazabilidadDrawerCtx.Provider>
  );
}

export function useTrazabilidadDrawer() {
  const ctx = useContext(TrazabilidadDrawerCtx);
  if (!ctx) {
    // Fallback no-op si alguien llama fuera del provider — evita crashes
    // durante desarrollo antes de integrar el provider en App.js.
    return {
      openHallazgo: () => {},
      openBudget: () => {},
      openOrder: () => {},
      openVoucher: () => {},
      closeAll: () => {},
      stack: [],
    };
  }
  return ctx;
}
